import { useCallback, useState } from 'react';
import { SmartLinkWidgetType } from 'fym-shared/src/smartlinks/enums';
import {
  BioLinkAutoAddPosition,
  BioLinkAutoAddResult,
  bioLinkInsertPosition,
  DEFAULT_BIO_LINK_SETTINGS,
} from 'fym-shared/src/smartlinks/bioLinkSettings';
import {
  useCreateWidgetMutation,
  useDeleteWidgetMutation,
  useGetBioLinkSettingsQuery,
  useGetSmartLinkQuery,
  usePublishSmartLinkMutation,
} from '../../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { useIsDemoAccount } from '../../../../accountSelector/useIsDemoAccount';
import { removeHttpAndSlash } from '../../../utils/removeHttpRegex';
import { DoneBioLinkState, doneBioLinkPageId, doneBioLinkState } from './doneBioLinkState';

export interface DoneBioLinkRow {
  state: DoneBioLinkState;
  /** The public address of the Bio Link with no scheme ("laziibee.music24.link"). */
  address: string;
  place: BioLinkAutoAddPosition;
  /** True after Undo in this visit: the row says "Not on your Bio Link · Add it". */
  isUndone: boolean;
  isBusy: boolean;
  hasError: boolean;
  undo: () => void;
  add: () => void;
  open: () => void;
}

/**
 * The data and the actions of the Bio Link row of Done (design smartlink-editor e2). Undefined: no
 * row (the demo artist, no Bio Link, an older API, the page still loads).
 */
export function useDoneBioLinkRow(
  accountId: number,
  itemUuid: string,
  result: BioLinkAutoAddResult | undefined
): DoneBioLinkRow | undefined {
  const router = useSMLRouter();
  const isDemo = useIsDemoAccount();
  const settings = useGetBioLinkSettingsQuery({ accountId }, { skip: isDemo });
  const pageId = isDemo ? null : doneBioLinkPageId(result, settings.data?.bioLinkSmartLinkId);
  const page = useGetSmartLinkQuery({ accountId, smartLinkId: pageId ?? 0 }, { skip: pageId === null });
  const [deleteWidget] = useDeleteWidgetMutation();
  const [createWidget] = useCreateWidgetMutation();
  const [publishSmartLink] = usePublishSmartLinkMutation();
  const [isUndone, setUndone] = useState(false);
  const [isBusy, setBusy] = useState(false);
  const [hasError, setError] = useState(false);
  const smartLink = page.data?.smartLink;
  const place = settings.data?.settings.position ?? DEFAULT_BIO_LINK_SETTINGS.position;

  const run = useCallback((work: () => Promise<void>) => {
    setBusy(true);
    setError(false);
    work()
      .catch((error: unknown) => {
        console.error(error);
        setError(true);
      })
      .finally(() => setBusy(false));
  }, []);

  // The delete and add routes change the draft only; the live page changes on Publish. The auto add
  // wrote the block to the live page and to the draft, so the draft has no other change when
  // `pendingChanges` is 0. Then this row publishes after its own change, and the live page loses (or
  // gets) only this block. With other changes in the draft (or a count we do not know), a publish
  // would also put those changes live, so the row does not publish and says "Removed from your
  // draft. Publish the Bio Link to take it off the page." A page that was never published has
  // nothing live to change, so the row never publishes it.
  const isDraftClean = smartLink?.publishedSmartLinkVersion !== null && smartLink?.pendingChanges === 0;

  const undo = useCallback(() => {
    if (smartLink === undefined || pageId === null) {
      return;
    }
    const state = doneBioLinkState(smartLink, itemUuid);
    if (state.kind !== 'live' && state.kind !== 'notLive') {
      return;
    }
    run(async () => {
      await deleteWidget({ accountId, smartLinkId: pageId, widgetUUID: state.widgetUuid }).unwrap();
      if (isDraftClean) {
        await publishSmartLink({ accountId, smartLinkId: pageId }).unwrap();
      }
      setUndone(true);
    });
  }, [accountId, deleteWidget, isDraftClean, itemUuid, pageId, publishSmartLink, run, smartLink]);

  const add = useCallback(() => {
    if (smartLink === undefined || pageId === null) {
      return;
    }
    const widgets = smartLink.recentSmartLinkVersion.widgets ?? [];
    run(async () => {
      await createWidget({
        accountId,
        smartLinkId: pageId,
        type: SmartLinkWidgetType.presave,
        data: { catalogItemUuid: itemUuid },
        position: bioLinkInsertPosition(widgets, place),
      }).unwrap();
      if (isDraftClean) {
        await publishSmartLink({ accountId, smartLinkId: pageId }).unwrap();
      }
      setUndone(false);
    });
  }, [accountId, createWidget, isDraftClean, itemUuid, pageId, place, publishSmartLink, run, smartLink]);

  const open = useCallback(() => {
    if (pageId !== null) {
      router.push(SMLRoutes.details, { accountId, smartLinkId: pageId });
    }
  }, [accountId, pageId, router]);

  if (smartLink === undefined || pageId === null) {
    return undefined;
  }
  return {
    state: doneBioLinkState(smartLink, itemUuid),
    address: removeHttpAndSlash(smartLink.shareUrl ?? ''),
    place,
    isUndone,
    isBusy,
    hasError,
    undo,
    add,
    open,
  };
}
